import * as React from "react";

import { deref } from "kanopi-components/utils/primitives";
import { toLocalStorage } from "kanopi-components/utils/browser";
import { useGlobal } from "kanopi-components/useGlobal";
import { parseQueryString } from "kanopi-components/utils/browser";

import api, { $, setBearer } from "kw/api";
import { userFields } from "kw/api/fragments";

type TuseAuthenticate = {
  lazy?: boolean;
  login?: boolean;
  now?: boolean;
  email?: string;
  password?: string;
  kanopiAuthenticatorJwt?: string;
};

/**
 * Authenticate a user or ensure a lazy user is created.
 *
 * useAuthenticate can authenticate an email and a password, or, if
 * `lazy` is true it will make sure there is a lazy user
 * available, no credentials needed. If `login` is true it will refresh
 * tokens based on a user logged in session in the BE. We can also use and upgrade
 * a JWT from the kanopi authentication platform.
 *
 * When a lazy user is requested and the user is already
 * authenticated, whether it be a lazy user or a full user, nothing
 * happens.
 */
export const useAuthenticate = ({
  email,
  password,
  lazy,
  login,
  now,
  kanopiAuthenticatorJwt,
}: TuseAuthenticate = {}) => {
  const [auth = {}, setAuth] = useGlobal("auth");
  const [loading, setLoading] = React.useState(now);
  async function authenticate(overrides = {}) {
    try {
      setLoading(true);
      const kanopiJwt = deref(
        overrides,
        "kanopiAuthenticatorJwt",
        kanopiAuthenticatorJwt
      );
      if (kanopiJwt) {
        await authenticateWithKanopiJwt(kanopiJwt, setAuth);
      } else if (deref(overrides, "lazy", lazy) || deref(overrides, "login", login)) {
        await refreshUser(setAuth);
      } else {
        await authenticateUser(
          deref(overrides, "email", email),
          deref(overrides, "password", password),
          setAuth
        );
      }
    } finally {
      setLoading(false);
    }
  }
  React.useEffect(() => {
    if (now) {
      authenticate();
    }
  }, []);
  return { auth, authenticate, loading };
};

const refreshUser = async (setAuth) => {
  const { distributionPartner } = parseQueryString({
    search: location.search,
  });
  const response = await api.mutation(
    {
      refreshUser: [
        {
          input: $("input", "RefreshUserInput"),
        },
        {
          user: userFields,
          token: true,
        },
      ],
    },
    {
      input: { distributionPartner },
    }
  );

  setBearer(response.refreshUser.token);
  toLocalStorage("auth", response.refreshUser);
  return await setAuth(response.refreshUser);
};

const authenticateUser = async (email, password, setAuth) => {
  const response = await api.mutation(
    {
      tokenAuth: [
        {
          email: $("email", "String!"),
          password: $("password", "String!"),
        },
        {
          user: userFields,
          token: true,
        },
      ],
    },
    {
      email,
      password,
    }
  );
  setBearer(response.tokenAuth.token);
  toLocalStorage("auth", response.tokenAuth);
  return await setAuth(response.tokenAuth);
};

/**
 * Authenticates a user with a Kanopi session JWT token
 *
 * This function performs authentication using a JWT obtained from the Kanopi
 * authentication system. It makes a GraphQL mutation call to the backend, which
 * validates the JWT and returns user information and a new authentication token.
 * The function then sets up the authentication state for the application.
 *
 * @param {string} sessionJwt - JWT token from the Kanopi authentication system
 * @param {Function} setAuth - State setter function to update auth context with
 *                              authentication results
 * @returns {Promise<Object>} Promise that resolves to the updated auth state
 * @throws {Error} If authentication fails or the JWT is invalid
 */
const authenticateWithKanopiJwt = async (sessionJwt, setAuth) => {
  const response = await api.mutation(
    {
      kanopiTokenAuth: [
        {
          sessionJwt: $("sessionJwt", "String!"),
        },
        {
          token: true,
          user: userFields,
        },
      ],
    },
    {
      sessionJwt,
    }
  );

  setBearer(response.kanopiTokenAuth.token);
  toLocalStorage("auth", response.kanopiTokenAuth);
  return await setAuth(response.kanopiTokenAuth);
};
